<template>
  <div>
    <div class="zoom-actions">
      <v-btn @click="zoomIn">+</v-btn>
      <v-btn class="ml-2" @click="zoomOut">-</v-btn>
      <v-btn class="ml-2" @click="reset">
        <v-icon class="mr-1" color="icon" small>mdi-restart</v-icon>
      </v-btn>
    </div>

    <!---JSON code-->
    <div class="json-action">
      <v-btn @click="collapseJsonPreview">
        <v-icon> mdi-code-json </v-icon>
      </v-btn>
    </div>

    <div v-if="displayJsonPreview" class="json-preview">
      <div class="json-preview__header">
        <div v-if="jsonFormatError" class="d-flex align-center mb-3">
          <span class="json-preview__format-error">Invalid JSON format!</span>
          <v-tooltip top>
            <template #activator="{ on }">
              <v-btn v-on="on" @click="undoJson" class="ml-3">
                <v-icon>mdi-undo</v-icon>
              </v-btn>
            </template>
            <p>Undo to previous valid JSON state</p>
          </v-tooltip>
        </div>
      </div>
      <code-input
        :class="{ 'code-error': jsonFormatError }"
        :key="jsonInputKey"
        v-model="multiStepFormDataJsonFormat"
        :title="''"></code-input>
    </div>
    <!---JSON code END -->

    <div class="panzoom-parent">
      <div ref="panzoom">
        <!--Form settings -->
        <div class="d-flex justify-center">
          <div class="form-data">
            <div class="title mb-2">Form Settings</div>
            <v-select
              class="mb-4 panzoom-ignore"
              v-model="multiStepFormData.formTemplate"
              :items="formTemplateOptions"
              :label="'Form template'"
              hide-details></v-select>
            <v-btn
              v-if="!showDescriptionEditor"
              class="d-flex mb-2"
              text
              outlined
              @click="showDescriptionEditor = true">
              Description Edit
            </v-btn>

            <div v-if="!showDescriptionEditor && multiStepFormData.formDescription">
              <div class="content-preview-label">Description preview</div>
              <div class="text-left content-preview" v-html="multiStepFormData.formDescription"></div>
            </div>

            <va-tinymce-input
              v-if="showDescriptionEditor"
              class="mb-4 w-full"
              :current-content="multiStepFormData.formDescription"
              :editor-height="120"
              live-chat-message-preset
              title="Form Description"
              :forced-root-block-attrs="{ class: 'live-chat-message' }"
              auto-resize
              @update="content => updateDescriptionContent(content)" />
            <VaColorPicker
              class="mb-4 panzoom-ignore"
              :title="'Text color'"
              mode="hexa"
              v-model="multiStepFormData.formTextColor"></VaColorPicker>
            <VaColorPicker
              class="mb-4 panzoom-ignore"
              :title="'Background color'"
              mode="hexa"
              v-model="multiStepFormData.formBackgroundColor"></VaColorPicker>
            <VaColorPicker
              class="mb-4 panzoom-ignore"
              :title="'Border color'"
              mode="hexa"
              v-model="multiStepFormData.formBorderColor"></VaColorPicker>
            <VaColorPicker
              class="mb-4 panzoom-ignore"
              :title="'Input button text color'"
              mode="hexa"
              v-model="multiStepFormData.inputButtonTextColor"></VaColorPicker>
            <VaColorPicker
              class="mb-4 panzoom-ignore"
              :title="'Input button background color'"
              mode="hexa"
              v-model="multiStepFormData.inputButtonBackgroundColor"></VaColorPicker>
            <VaColorPicker
              class="mb-4 panzoom-ignore"
              :title="'Input button border color'"
              mode="hexa"
              v-model="multiStepFormData.inputButtonBorderColor"></VaColorPicker>
            <v-text-field
              class="mb-4 panzoom-ignore"
              v-model="multiStepFormData.formMaxWidth"
              :label="'Form max width'"
              persistent-placeholder
              :placeholder="'e.g. 380px'"></v-text-field>
            <v-text-field
              class="mb-4 panzoom-ignore"
              v-model="multiStepFormData.formPadding"
              :label="'Form padding'"
              persistent-placeholder
              :placeholder="'e.g. 20px or 10px 20px'"></v-text-field>
            <div class="d-flex justify-center">
              <v-btn :color="'primary'" class="mr-2" v-if="displayAddPageBtn" @click="addPage">Add page</v-btn>
            </div>
          </div>
        </div>
        <!--Form settings END-->
        <!--Pages -->
        <div class="page-tree">
          <MultiStepFormTree :key="multiStepFormTreeKey" :page-tree="multiStepFormData.pages" :form-template="multiStepFormData.formTemplate" :form-text-color="multiStepFormData.formTextColor"></MultiStepFormTree>
        </div>
        <!--Pages END -->
      </div>
    </div>
  </div>
</template>

<script>
  // Components
  import MultiStepFormTree from "@/components/shortcodes/multistepform/MultiStepFormTree.vue";
  import Panzoom from "@panzoom/panzoom";
  import CodeInput from "@/components/custom/CodeInput";
  import VaColorPicker from "@/components/custom/VaColorPicker";
  import VaTinymceInput from "@/components/custom/VaTinymceInput";
  // Helpers
  import debounce from "lodash.debounce";
  import cloneDeep from "lodash/cloneDeep";
  import { convertAssetsPathToAbsoluteLinks } from "@/utility/convertAssetsPaths";
  import { traverseTree } from "@/utility/traverseTree";
  import { syncTelHrefErrors } from "@/utility/validateLiveChatTelHrefs";
  // Constants
  import { PAGE, TEMPLATE_PRESETS } from "@/components/shortcodes/multistepform/constant";
  export default {
    name: "MultiStepFormDialog",
    components: { MultiStepFormTree, CodeInput, VaColorPicker, VaTinymceInput },
    data() {
      return {
        panzoomInstance: null,
        skipTemplateWatch: false,
        formTemplateOptions: [
          { text: "Dark Form", value: "dark" },
          { text: "White Form", value: "white" },
        ],
        multiStepFormData: {
          formTemplate: "dark",
          formDescription: "",
          formTextColor: TEMPLATE_PRESETS.dark.formTextColor,
          formBackgroundColor: TEMPLATE_PRESETS.dark.formBackgroundColor,
          formBorderColor: TEMPLATE_PRESETS.dark.formBorderColor,
          inputButtonTextColor: TEMPLATE_PRESETS.dark.inputButtonTextColor,
          inputButtonBackgroundColor: TEMPLATE_PRESETS.dark.inputButtonBackgroundColor,
          inputButtonBorderColor: TEMPLATE_PRESETS.dark.inputButtonBorderColor,
          formMaxWidth: "380px",
          formPadding: "",
          pages: [],
        },
        /**
         * When editing JSON input (code-input), user might make the JSON inside of the code-input invalid;
         * jsonFormatError is true if JSON format inside of the code-input is incorrect (not parseable using JSON.parse)
         */
        displayJsonPreview: false,
        jsonFormatError: false,
        jsonInputKey: 0,
        multiStepFormTreeKey: 0,
        showDescriptionEditor: false,
      };
    },
    computed: {
      displayAddPageBtn() {
        return !this.multiStepFormData.pages?.length;
      },
      multiStepFormDataJsonFormat: {
        get() {
          const displayData = this.multiStepFormData;

          return JSON.stringify(displayData, null, " ");
        },
        set(value) {
          let parsedJson;
          try {
            this.jsonFormatError = false;
            parsedJson = JSON.parse(value);
          } catch (e) {
            this.jsonFormatError = true;
          }

          if (!this.jsonFormatError) {
            /**
             * Re-render the tree when json code input is changed (Required so that tinymce inputs update correctly);
             * Might need optimization in case of large page tree
             */
            this.rerenderPageTree();
            // Assign separately to keep reactivity (!!!)
            this.multiStepFormData.pages = parsedJson.pages;
            this.multiStepFormData.formTemplate = parsedJson.formTemplate || "dark";
            this.multiStepFormData.formDescription = parsedJson.formDescription || "";
            const jsonPreset = TEMPLATE_PRESETS[parsedJson.formTemplate] || TEMPLATE_PRESETS.dark;
            this.multiStepFormData.formTextColor = parsedJson.formTextColor || jsonPreset.formTextColor;
            this.multiStepFormData.formBackgroundColor = parsedJson.formBackgroundColor || jsonPreset.formBackgroundColor;
            this.multiStepFormData.formBorderColor = parsedJson.formBorderColor || jsonPreset.formBorderColor;
            this.multiStepFormData.inputButtonTextColor = parsedJson.inputButtonTextColor || jsonPreset.inputButtonTextColor;
            this.multiStepFormData.inputButtonBackgroundColor = parsedJson.inputButtonBackgroundColor || jsonPreset.inputButtonBackgroundColor;
            this.multiStepFormData.inputButtonBorderColor = parsedJson.inputButtonBorderColor || jsonPreset.inputButtonBorderColor;
            this.multiStepFormData.formMaxWidth = parsedJson.formMaxWidth || "380px";
            this.multiStepFormData.formPadding = parsedJson.formPadding || "";
            this.showDescriptionEditor = !parsedJson.formDescription;
          }
        },
      },
    },
    watch: {
      "multiStepFormData.formTemplate"(template, oldTemplate) {
        if (this.skipTemplateWatch) {
          this.skipTemplateWatch = false;
          return;
        }
        const preset = TEMPLATE_PRESETS[template];
        if (!preset) return;

        const applyColors = () => {
          this.multiStepFormData.formTextColor = preset.formTextColor;
          this.multiStepFormData.formBackgroundColor = preset.formBackgroundColor;
          this.multiStepFormData.formBorderColor = preset.formBorderColor;
          this.multiStepFormData.inputButtonTextColor = preset.inputButtonTextColor;
          this.multiStepFormData.inputButtonBackgroundColor = preset.inputButtonBackgroundColor;
          this.multiStepFormData.inputButtonBorderColor = preset.inputButtonBorderColor;

          if (this.multiStepFormData.pages?.length) {
            traverseTree(this.multiStepFormData.pages, node => {
              if (node.pageType === "action") {
                node.backgroundColor = preset.actionBackgroundColor;
                node.textColor = preset.actionTextColor;
                node.borderColor = preset.actionBorderColor;
                node.padding = preset.actionPadding;
              }
              if (node.pageType === "inputQuestion" || node.pageType === "zipInputQuestion") {
                node.buttonColor = preset.inputButtonBackgroundColor;
                node.buttonTextColor = preset.inputButtonTextColor;
                node.buttonBorderColor = preset.inputButtonBorderColor;
              }
              if (node.pageType === "page") {
                if (node.progressBar) {
                  node.progressBar.color = preset.progressBarColor;
                }
                if (node.checkingElement) {
                  node.checkingElement.textColor = preset.checkingElementTextColor;
                  node.checkingElement.checkIconColor = preset.checkingElementIconColor;
                }
              }
            });
            this.rerenderPageTree();
          }
        };

        if (this.multiStepFormData.pages?.length) {
          if (confirm("Changing the template will update all button and text colors in the tree. Continue?")) {
            applyColors();
          } else {
            this.skipTemplateWatch = true;
            this.$nextTick(() => {
              this.multiStepFormData.formTemplate = oldTemplate;
            });
          }
        } else {
          applyColors();
        }
      },
      multiStepFormData: {
        handler(newValue) {
          this.updateValue(newValue);
        },
        deep: true,
      },
    },
    beforeMount() {
      this.setupReceiveDataListener();
      this.requestData();
    },
    mounted() {
      this.panzoomInstance = Panzoom(this.$refs.panzoom, {
        canvas: true,
        /**
         * Ignores all panzoom events on elements with provided class;
         * This is required because sometimes we use mouse click and drag to select things (text in the input for example)
         * This prevents panzoom from moving the view when trying to select text
         */
        excludeClass: "panzoom-ignore",
        /**
         * By default, panzoom will call event.preventDefault() and event.stopPropagation() on every pan event.
         * This prevents colorpicker from being closed on outside click.
         * Override the default handleStartEvent function to solve this.
         */
        handleStartEvent: () => {},
      });

      // Set appropriate height for panzoom wrapper. Set ti to window innerheight
      this.$refs.panzoom && (this.$refs.panzoom.style.height = `${window.innerHeight - 24}px`);

      const parent = this.$refs.panzoom.parentElement;

      this.wheelHandler = event => {
        if (!event.shiftKey) {
          this.panzoomInstance.pan(-event.deltaX, -event.deltaY, { relative: true });

          return;
        }
        this.panzoomInstance.zoomWithWheel(event);
      };
      parent.addEventListener("wheel", this.wheelHandler);
    },
    methods: {
      addPage() {
        const page = cloneDeep(PAGE);
        this.multiStepFormData.pages.push(page);
      },
      rerenderPageTree: debounce(function () {
        this.multiStepFormTreeKey++;
      }, 1000),
      reset() {
        this.panzoomInstance.reset();
      },
      zoomIn() {
        this.panzoomInstance.zoomIn();
      },
      zoomOut() {
        this.panzoomInstance.zoomOut();
      },
      collapseJsonPreview() {
        this.displayJsonPreview = !this.displayJsonPreview;
      },
      /**
       * Updates the JSON input to previous valid state.
       */
      undoJson() {
        const displayData = this.multiStepFormData;
        this.multiStepFormDataJsonFormat = JSON.stringify(displayData, null, " ");
        this.jsonInputKey++;
      },
      updateValue(val) {
        window.parent.postMessage(
          {
            mceAction: "updateMultiStepFormData",
            data: val,
          },
          origin,
        );
      },
      syncTelHrefErrors() {
        syncTelHrefErrors(this.multiStepFormData.pages, this);
      },
      setupReceiveDataListener() {
        window.onmessage = message => {
          if (!message.data || typeof message.data !== "object") return;

          if (message.data.type === "telHrefValidationFailed") {
            this.syncTelHrefErrors();
            return;
          }

          if (message.data.type === "sendData") {
            const { data } = message.data;
            traverseTree(data.pages, page => {
              if (page.content) {
                page.content = convertAssetsPathToAbsoluteLinks(page.content);
              }

              if (page.text) {
                page.text = convertAssetsPathToAbsoluteLinks(page.text);
              }

              if (page.value) {
                page.value = convertAssetsPathToAbsoluteLinks(page.value);
              }
            });

            if (data.formDescription) {
              data.formDescription = convertAssetsPathToAbsoluteLinks(data.formDescription);
            }

            const preset = TEMPLATE_PRESETS[data.formTemplate] || TEMPLATE_PRESETS.dark;
            this.skipTemplateWatch = true;
            this.multiStepFormData = {
              ...data,
              formTemplate: data.formTemplate || "dark",
              formTextColor: data.formTextColor || preset.formTextColor,
              formBackgroundColor: data.formBackgroundColor || preset.formBackgroundColor,
              formBorderColor: data.formBorderColor || preset.formBorderColor,
              inputButtonTextColor: data.inputButtonTextColor || preset.inputButtonTextColor,
              inputButtonBackgroundColor: data.inputButtonBackgroundColor || preset.inputButtonBackgroundColor,
              inputButtonBorderColor: data.inputButtonBorderColor || preset.inputButtonBorderColor,
              formMaxWidth: data.formMaxWidth || "380px",
              formPadding: data.formPadding || "",
            };
            this.showDescriptionEditor = !data.formDescription;
          }
        };
      },
      requestData() {
        window.parent.postMessage(
          {
            mceAction: "requestData",
          },
          origin,
        );
      },
      updateDescriptionContent(content) {
        this.multiStepFormData.formDescription = content;
      },
    },
    beforeDestroy() {
      if (this.wheelHandler) {
        const parent = this.$refs.panzoom?.parentElement;
        if (parent) {
          parent.removeEventListener("wheel", this.wheelHandler);
        }
      }
    },
  };
</script>

<style lang="scss" scoped>
  @import "@core/preset/preset/typography/index.scss";
  @import "@core/preset/preset/functions.scss";
  .zoom-actions {
    position: absolute;
    top: 20px;
    left: 20px;
    z-index: 100;
  }
  .json-action {
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 102;
  }

  .title {
    color: get-color("subtitle");
    @include heading-4();
  }

  .form-data {
    border: 1px dashed get-color("primary");
    border-radius: 5px;
    background: get-color("element-bg-100");
    padding: 5px 10px;
    max-width: 600px;
    min-width: 600px;
  }

  .page-tree {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
  }

  .json-preview {
    position: absolute;
    top: 0;
    left: 5px;
    right: 5px;
    bottom: 5px;
    z-index: 101;
    background-color: get-color("element-bg-200");
    &__header {
      display: flex;
      background: get-color("element-bg-200");
      padding: 70px 20px 0;
    }
    &__format-error {
      color: get-color("error");
      @include text-body-1-bold();
    }
  }
  .code-error {
    border: 3px dashed get-color("error") !important;
  }
</style>
